import { config } from "./constants.ts";
import { ErrorCode } from "./error-handler-util.ts";

import moment from "moment";
import { DateUtil } from "@fundwave/ui-utils/src/DateUtil.js";
import { EntityType, VariableGroupBy } from "@fundwave/types";

class Utils {

  static MCPClientAPIClient = null;

  static filterByDate(dataArray, toDate, fromDate, dataType) {
    let filteredData = [];
    if (!dataArray) dataArray = [];
    if (!fromDate) fromDate = 1; //01-01-1900
    if (!toDate) toDate = 109939; //31-12-2200

    if (dataType === "rounds") {
      for (let index in dataArray) {
        let round = { ...dataArray[index] };
        if (round.date && round.date <= toDate && round.date >= fromDate) filteredData.push(dataArray[index]);
      }
    }

    return filteredData;
  }

  //@Deprecated
  static getCustomFieldValues(assetFields, customFields) {
    if (!assetFields || !customFields) return {};

    assetFields = JSON.parse(assetFields);
    let customFieldValues = {};

    for (let key of Object.keys(customFields)) {
      if (assetFields[key]) {
        let keyName = customFields[key].Name.replaceAll(" ", "").toLowerCase();
        customFieldValues[keyName] = assetFields[key];
      }
    }

    return customFieldValues;
  }

  static setHeaders(data, propertyArray, horizontal, functionType) {

    const propertyNames = [];
    for (const property of propertyArray) {
      let fieldInfo;
      if (functionType && Utils.fields[functionType]) {
        fieldInfo = Utils.fields[functionType].find(field => field.name.toLowerCase() === property.toLowerCase());
      }
      if (fieldInfo && fieldInfo.title) propertyNames.push(fieldInfo.title);
      else propertyNames.push(Utils.capitalizeWord(property));
    }

    if (horizontal) {
      for (let index in data) {
        data[index].unshift(propertyNames[index]);
      }
    } else {
      data.unshift(propertyNames);
    }

    return data;
  }

  static buildContext(investorId, assetId, fundId, periodId, scenarioId, fromDate, toDate, investorTransactionTypes = null) {
    let context = {};
    if (investorId) {
      if (Array.isArray(investorId)) {
        const investorIds = investorId.map((id) => ({ id: String(id) }));
        context = { ...context, investors : { investor: investorIds}};
      } else {
        if (typeof investorId == Number) investorId = String(investorId);
        context["investorId"] = investorId;
      }
    }
    if (assetId) {
      if (typeof assetId == Number) assetId = String(assetId);
      context["assetId"] = assetId;
    }
    if (fundId) {
      if (Array.isArray(fundId)) {
        const fundIds = fundId.map((id) => ({ fundId: String(id) }));
        context = { ...context, funds: { fund: fundIds } };
      } else {
        if (typeof fundId == Number) fundId = String(fundId);
        context["fundId"] = fundId;
      }
    }

    let periodType = {},
      type;
    if (periodId) periodType["id"] = periodId;

    type = Utils.getPeriodTypeFromId(periodId);
    if (type) periodType["type"] = type;

    if (!(Object.keys(periodType).length === 0 && periodType.constructor === Object)) {
      context["periodType"] = periodType;
    }

    let scenario = {};
    if (scenarioId) {
      scenario["scenarioId"] = scenarioId;
      context["scenario"] = scenario;
    }

    if (fromDate) context["fromDate"] = fromDate;

    if (toDate) context["toDate"] = toDate;

    if (investorTransactionTypes) {
      context = {
        ...context,
        investorTransactionTypes: {
          investorTransactionType: investorTransactionTypes.map((type) => ({ type: String(type) })),
        },
      };
    }

    context = JSON.stringify(context);

    return context;
  }

  static getPeriodTypeFromId(periodId) {
    let type;
    switch (periodId) {
      case "SI":
        type = "SINCE INCEPTION";
        break;
      case "LQ":
        type = "LAST QUARTER";
        break;
      case "LTM":
        type = "LAST 12 MONTHS";
        break;
      case "LM":
        type = "LAST MONTH";
        break;
      case "YTD":
        type = "YEAR TO DATE";
        break;
      // default:
      // throw new Error("Choose from SI(Since Inception), LQ(Last Quarter), LM(Last Month), LTM(Last 12 Months) or YTD(Year to Date)");
    }
    return type;
  }

  static formatVariableValueResponse(variableValues, groupBy, requestContext) {
    const codes = requestContext["codes"] || [];

    if (!groupBy) {
      const formattedValues = [];
      for (let code of codes) {
        let item = variableValues.find((item) => item.code?.toLowerCase() === code?.toLowerCase());
        if (!item) item = { code: code };
        if (item.dateList) item.value = item.dateList[0];
        item.value = isNaN(item.value) ? item.value : Number(item.value);
        formattedValues.push(item);
      }
      return formattedValues;
    }

    //sort returnArray bases on input groupBy ids
    let entityIds = [];
    let entityKey = "";
    
    switch (groupBy) {
      case VariableGroupBy.Fund:
        entityIds = requestContext.fundIds || [];
        entityKey = "fundId";
        break;
      case VariableGroupBy.Asset:
        entityIds = requestContext.assetIds || [];
        entityKey = "assetId";
        break;
      case VariableGroupBy.InvestorCode:
        entityIds = requestContext.investorCodes || [];
        entityKey = "investorCode";
        break;
      case VariableGroupBy.Assetaccount:
        entityIds = requestContext.assetAccountIds || [];
        entityKey = "assetAccountId";
        break;
    }

    const returnArray = [];

    for (const inputItemIndex in entityIds) {
      const entityId = entityIds[inputItemIndex];
      const variableValuesForEntity = variableValues[entityId] || [];

      if (!returnArray[inputItemIndex]) returnArray[inputItemIndex] = [];

      for (const code of codes) {
        let variableValue = variableValuesForEntity.find((item) => item.code?.toLowerCase() === code?.toLowerCase());
        if (!variableValue) variableValue = { code };
        if (variableValue.dateList) variableValue.value = variableValue.dateList[0];
        
        variableValue.value = isNaN(variableValue.value) ? variableValue.value : Number(variableValue.value);
        variableValue[entityKey] = entityId;
        returnArray[inputItemIndex].push(variableValue);
      }

      if (!returnArray[inputItemIndex]) {
        returnArray[inputItemIndex].push({ value: 0, [entityKey]: entityId });
      }
    }

    variableValues = returnArray;
    return variableValues;
  }

  static formatArray(inputData, properties, horizontal, showHeaders, functionType) {
    let returnArray = [];

    const propertyArray = properties ? properties.split(",") : [];
    const formattedProperties = propertyArray.map((property) => property?.trim()?.toLowerCase()?.replaceAll(" ", ""));
    //if any property is 2level deep, flatten listItems
    let toFlatten = propertyArray.find((property) => property.split(".").length > 1);
    if (toFlatten) inputData = Utils.flattenList(inputData);

    //make all properties of object to lowerCase
    inputData = inputData.map((dataItem) => Utils.formatObjectKeys(dataItem));

    if (horizontal == 1) returnArray = Utils.setHorizontalData(inputData, formattedProperties, functionType);
    else returnArray = Utils.setVerticalData(inputData, formattedProperties, functionType);

    if (showHeaders) returnArray = Utils.setHeaders(returnArray, propertyArray, horizontal, functionType);

    return returnArray;
  }

  static setHorizontalData(inputData, properties, functionType) {

    let result = [];

    for (var property of properties) {
      var details = [];

      for (var obj of inputData) {
        var data = Utils.format(obj, property, functionType);

        if (!data && data !== 0) data = "";

        details.push(data);
      }

      result.push(details);
    }

    return result;
  }

  static setVerticalData(inputData, properties, functionType) {

    let result = [];

    for (let obj of inputData) {
      var details = [];
      if (["variableValues", "metricValues"].includes(functionType) && Object.keys(obj).includes("0")) obj = Object.values(obj);

      for (let property of properties) {
        let data;
        if (obj.length > 0) {
          let dataArray = obj.map((objItem) => Utils.format(objItem, property, functionType));
          data = dataArray;
        } else data = Utils.format(obj, property, functionType);

        if (!data && data !== 0) data = "";

        if (["variableValues", "metricValues"].includes(functionType) && data.length > 0) details.push(...data);
        else details.push(data);
      }

      result.push(details);
    }

    return result;
  }

 static format(obj, property, functionType) {
    if (!property) property = "name";

    if (!obj) return obj;

    // Check if there's an accessor defined in Utils.fields for the given functionType
    let accessor;
    if (functionType && Utils.fields[functionType]) {
      const fieldInfo = Utils.fields[functionType].find(field => field.name.toLowerCase() === property);
      if (fieldInfo && fieldInfo.accessor) {
        accessor = fieldInfo.accessor.toLowerCase().replaceAll(" ", "");
      }
    }

    let data;
    if (accessor) {
      let props = accessor.split('.');
      data = obj;
      for (const prop of props) {
        if (!data) break;
        data = data[prop];
      }
    
    } else {
      data = obj[property];

      if (!data && data !== 0) data = obj[`${property}.name`];
    }

    if (!data && data !== 0) return "";

    if (data && isNaN(data) && moment(data, moment.ISO_8601, true).isValid()) return Utils.formatDate(data);

    if (data && typeof data === "object") {
      if (data["name"]) data = data["name"];
      else if (data["id"]) data = data["id"];
      else if (data["code"]) data = data["code"];
      else data = "";
    }

    return data;
  }


  static formatDate(value) {
    if (!value) return;
    let momentDateObj = moment(value);

    var excelDate = 25569.0 + (momentDateObj.unix() + momentDateObj.utcOffset() * 60) / (60 * 60 * 24);

    excelDate = +excelDate.toString().substr(0, 5);

    return excelDate;
  }

  static getFormattedDate(date, timezone = true) {
    if (timezone) return Utils.getJSDateFromExcel(date);
    return Utils.getJSDateFromExcel(date)?.split("T")[0];
  }

  static getJSDateFromExcel(excelDate) {
    let date = new Date((excelDate - (25567 + 2)) * 86400 * 1000);
    return date.toISOString();
  }

  static getExcelDateFromJS(jsDate, timezone = false) {
    if (!jsDate) return;
    if (!["string", "number"].includes(typeof jsDate)) jsDate = jsDate.toISOString();

    if (timezone) jsDate = DateUtil.formatDate(jsDate, DateUtil.FORMATS.US_SHORT_DATE, true);
    return Utils.formatDate(jsDate);
  }

  static getJsDateFromGSheet(sheetDate) {
    if (typeof sheetDate === "string") sheetDate = new Date(sheetDate);
    return Utilities.formatDate(sheetDate, SpreadsheetApp.getActive().getSpreadsheetTimeZone(), "yyyy-MM-dd");
  }

  static flattenList(list) {
    for (let index in list) list[index] = Utils.flattenObject(list[index]);

    return list;
  }

  static transposeOfMatrix(arr) {
    let transposeArray = [[]];

    arr.forEach((row, i) => {
      arr[i].forEach((cell, j) => {
        if (!transposeArray[j]) transposeArray[j] = [];
        transposeArray[j][i] = arr[i][j];
      });
    });

    return transposeArray;
  }

  static convert2DArrayto1DArray(data) {
    let result = data.flat(1);

    result = result.map((item) => item.toString().split(",")).flat(1);

    return result;
  }

  static flattenObject(obj, parent, result = {}) {
    for (let key in obj) {
      let propName = parent ? parent + "." + key : key;

      if (typeof obj[key] == "object") Utils.flattenObject(obj[key], propName, result);
      else result[propName] = obj[key];
    }
    return result;
  }

  static getPropertiesInStringFormat(properties, defaultProperty) {
    if (!defaultProperty) defaultProperty = "name";

    let stringProperties = "";

    if (!properties) properties = [defaultProperty];

    stringProperties = properties.flat(1).join(",");

    if (!stringProperties || stringProperties.length < 1) stringProperties = defaultProperty;

    return stringProperties;
  }

  static formatObjectKeys(obj, result = {}) {
    for (let key in obj) {
      const formattedKey = typeof key === "string" ? key.trim().toLowerCase().replaceAll(" ", "") : key;
      if (typeof obj[key] == "object") result[formattedKey] = Utils.formatObjectKeys(obj[key]);
      else {
        if (typeof key == "string") result[formattedKey] = obj[key];
        else result[key] = obj[key];
      }
    }
    return result;
  }

  static capitalizeWordList(properties) {
    let formattedProperties = [];

    for (let property of properties) {
      let formattedProperty = Utils.capitalizeWord(property);
      formattedProperties.push(formattedProperty);
    }

    return formattedProperties;
  }

  static capitalizeWord(property) {
    let words = property.split(".");
    let formattedWords = [];

    for (let word of words) {
      let formattedWord = word.charAt(0).toUpperCase() + word.slice(1);
      formattedWords.push(formattedWord);
    }

    let formattedProperty = formattedWords.join(".");
    return formattedProperty;
  }

  static async prepareOpenAPIClient(apiClient) {
    const instanceUrl = localStorage.getItem("team");
    const teamDomainOnly = instanceUrl?.replace(/^https?:\/\//i, "").replace(/\/$/, "");

    const domainId = localStorage.getItem("dealflowDomainId");
    const teamId = localStorage.getItem("pipelineId");

    switch (apiClient) {
      case Utils.APIClient.fundwave:
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;

        const { OpenAPIClient } = await import("@fundwave/openapi-client/src/index.js");
        OpenAPIClient.oidcClient.setBaseUrl(config.openIdBaseUrl);
        OpenAPIClient.setBaseUrl(`${instanceUrl}/services/openapi`);

        const apiClient = await import("@fundwave/openapi-client/src/index.js");
        return apiClient;

      case Utils.APIClient.note:
        if (!teamId || !domainId) throw ErrorCode.MissingTeamUrl;

        const { NoteAPIClient } = await import("@fundwave/openapi-client/note/src/index.js");
        NoteAPIClient.oidcClient.setBaseUrl(config.dealflowUrl);
        NoteAPIClient.setBaseUrl(config.noteServiceUrl);
        if (config.noteServiceColor) NoteAPIClient.setAdditionalHeaders({ "x-fw-color": config.noteServiceColor });

        return await import("@fundwave/openapi-client/note/src/index.js");

      case Utils.APIClient.activity:
        
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;

        const { ActivityAPIClient } = await import("@fundwave/openapi-client/activity/src/index.js");
        ActivityAPIClient.oidcClient.setBaseUrl(config.openIdBaseUrl);
        ActivityAPIClient.setBaseUrl(`${instanceUrl}/api/activity`);

        return await import("@fundwave/openapi-client/activity/src/index.js");

      case Utils.APIClient.instrument:
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;

        const { OpenAPIClient : InstrumentAPIClient } = await import("@fundwave/openapi-client/instrument/src/index.js");
        InstrumentAPIClient.oidcClient.setBaseUrl(config.openIdBaseUrl);
        InstrumentAPIClient.setBaseUrl(config.instrumentServiceUrl);
        if (config.instrumentServiceColor) InstrumentAPIClient.setAdditionalHeaders({ "x-fw-color": config.instrumentServiceColor, "x-fw-team": teamDomainOnly });

        return await import("@fundwave/openapi-client/instrument/src/index.js");

      case Utils.APIClient.customField:
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;

        const { CustomFieldAPIClient } = await import("@fundwave/openapi-client/customField/src/index.js");
        CustomFieldAPIClient.oidcClient.setBaseUrl(config.fundwaveIdBaseUrl);
        CustomFieldAPIClient.setBaseUrl(config.customFieldServiceUrl);
        if (config.customFieldServiceColor) CustomFieldAPIClient.setAdditionalHeaders({ "x-fw-color": config.customFieldServiceColor, "x-fw-team": teamDomainOnly });

        return await import("@fundwave/openapi-client/customField/src/index.js");

      case Utils.APIClient.chatbot:
        const { ChatbotAPIClient } = await import("@fundwave/openapi-client/chatbot/src/index.js");
        ChatbotAPIClient.oidcClient.setBaseUrl(config.openIdBaseUrl);
        ChatbotAPIClient.setBaseUrl(config.chatbotUrl);

        return await import("@fundwave/openapi-client/chatbot/src/index.js");
      
      case Utils.APIClient.mcpClient: {
        if (!Utils.MCPClientAPIClient) {
          const { MCPClientAPIFW } = await import("@fundwave/openapi-client/mcpClient/src/index.js");

          const client = new MCPClientAPIFW("mcpclient");
          client.oidcClient.setBaseUrl(config.openIdBaseUrl);
          client.setBaseUrl(config.mcpClientUrl);

          Utils.MCPClientAPIClient = client;
        }

        return Utils.MCPClientAPIClient;
      }

      case Utils.APIClient.lead:
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;

        const { OpenAPIClient: LeadAPIClient } = await import("@fundwave/openapi-client/deal/src/index.js");
        LeadAPIClient.oidcClient.setBaseUrl(config.openIdBaseUrl);
        LeadAPIClient.setBaseUrl(config.dealServiceUrl);
        if (config.dealServiceColor) LeadAPIClient.setAdditionalHeaders({ "x-fw-color": config.dealServiceColor });

        return await import("@fundwave/openapi-client/deal/src/index.js");

      default:
        if (!instanceUrl) throw ErrorCode.MissingTeamUrl;
        return instanceUrl;
    }
  }

  static handleProperties(properties, defaultProp) {
    if (!properties) properties = [defaultProp || "name"];
    return properties.flat(1).join(",");
  }

  static getMapFromArray(array = [], key = "id") {
    if (!array || array.length === 0) return {};

    const map = {};
    for (const item of array) {
      if (!item || !item?.[key]) continue;
      map[item[key]] = item;
    }
    return map;
  }

  static buildUrl(endpoint, queryParams = {}) {
    const instanceUrl = localStorage.getItem("team");
    let url = `${instanceUrl}/services${endpoint}`;

    const paramString = Object.keys(queryParams)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(queryParams[key]))
      .join("&");

    return `${url}?${paramString}`;
  }

  static getMicroservicePayload(contextIds, contextType, teamId, teamType) {
    const instanceUrl = localStorage.getItem("team");

    return {
      context: {
        id: contextIds ?? [],
        type: contextType ?? "ASSET"
      },
      team: {
        id: teamId ?? instanceUrl.replace("https://", ""),
        type: teamType ?? "INSTANCE"
      }
    };
  }

  static async getIdFromCategoryName(category, type) {
    let updateTypes;

    category = category.toLowerCase();

    if (type === "asset") {
      const { AssetAPI } = await import("../../apiclient/asset.js");
      updateTypes = await AssetAPI.getAssetUpdateTypes();
    } else if (type === "fund") {
      const { FundAPI } = await import("../../apiclient/fund.js");
      updateTypes = await FundAPI.getFundUpdateTypes();
    } else if (type === "submission") {
      const { AssetAPI } = await import("../../apiclient/asset.js");
      updateTypes = await AssetAPI.getSubmissionUpdateTypes();
    }

    updateTypes = updateTypes.map((updateType) => {
      return { ...updateType, type: updateType.type.toLowerCase() };
    });

    const updateType = updateTypes.find((updateType) => updateType.type === category);

    const categoryId = updateType ? updateType.id : undefined;

    return categoryId;
  }

  static getCustomFieldValue(fieldValue, field) {
    const { datatype, options } = field;
    let value = fieldValue?.value;
    if (!datatype || !value) return null;
    switch (datatype?.type) {
      case "date":
        return value ? DateUtil.formatDate(value, DateUtil.FORMATS.DEFAULT, true) : undefined;
      case "radio":
      case "dropdown":
        return (datatype.format === "multi" ? value || [] : [value])
          ?.map((field) => (options || []).find((option) => option.id === field)?.value)
          ?.toString();
      case "file":
        return (value || []).map((value) => value?.name)?.toString();
      default:
        return value || undefined;
    }
  }

  /**
   * Fetch custom field values for the given context and merge them into entities
   * @param {Object} context - Context for which custom field values are to be fetched (e.g., { type: "ASSET" }, { type: "INVESTOR" })
   * @param {Array} entities - Array of entities to enrich with custom field values
   * @param {Array} propertyArray - Array of property names to check
   * @param {number} [toDate] - Optional date to fetch custom field values as of this date
   * @param {Object} [secondaryContext] - Optional secondary context for fetching custom field values
   * @returns {Array} Entities with custom field values merged in
   */
  static async prepareCustomFieldValues(context, entities, propertyArray, toDate, secondaryContext) {
    const { CustomFieldAPIClient } = await Utils.prepareOpenAPIClient(Utils.APIClient.customField);
    const { team } = Utils.getMicroservicePayload();
    const requestBody = {
      context: secondaryContext ? context : { type: context.type },
      team: { id: `https://${team.id}`, type: team.type },
      ...(secondaryContext ? { secondaryContext: { type: secondaryContext.type } } : {}),
    };

    const allCustomFields = await CustomFieldAPIClient.getCustomFields(requestBody);

    const properties = propertyArray.map(prop => prop.trim().toLowerCase().replaceAll(" ", ""));
    const customFields = allCustomFields.filter((field) => {
        const fieldKey = field?.name;
        return  properties.includes(fieldKey?.trim()?.toLowerCase()?.replaceAll(" ", ""));
      }
    );

    if (!customFields.length) return entities;

    const fieldValuesRequest = {
      team: requestBody.team,
      context: {
        ...requestBody.context,
        id: context.id ? [context.id] : entities.map((entity) => entity?.id?.toString())
      },
      ...(secondaryContext ? { secondaryContext } : {}),
      ...(toDate ? { period: { date: new Date(Utils.getJSDateFromExcel(toDate)) } } : {})
    };

    const fieldValues = await CustomFieldAPIClient.getFieldValues(fieldValuesRequest);

    const fieldValuesByEntityId = fieldValues.reduce((map, fieldValue) => {
      const entityId = fieldValue?.secondaryContext?.id ?? fieldValue?.context?.id;
      map.set(entityId, fieldValue);
      return map;
    }, new Map());

    return entities.map((entity) => {
      const entityId = entity?.id?.toString();
      const entityFieldValue = fieldValuesByEntityId.get(entityId) || {};

      const entityWithCustomFieldValues = {};
      customFields.forEach((customField) => {
        const fieldKey = customField?.name;
        const entityValue = entity?.[fieldKey];
        const fieldValue = (entityValue === null || entityValue === undefined) ? entityFieldValue?.fields?.[customField.id] : { value: entityValue };

        entityWithCustomFieldValues[fieldKey] = Utils.getCustomFieldValue(fieldValue, customField);
      });

      return { ...entity, ...entityWithCustomFieldValues };
    });
  }
}

Utils.APIClient = {
  fundwave: "fundwave",
  activity: "activity",
  captable: "captable",
  note: "note",
  instrument: "instrument",
  customField: "customField",
  chatbot: "chatbot",
  mcpClient: "mcpClient",
  lead: "lead"
};

Utils.assetFundFields = ["acquisitiondate", "fundcommitment", "fundfdstake", "exitdate", "commitment", "fdstake"];
Utils.assetBasicFields = ["id", "name", "currency", "currencycode", "country", "sector", "subsector", "investmentrole", "dealtype", "assettype", "manager", "ceo", "cfo", "financialyearend"];

Utils.assetFields = [
  { name: "id", title: "Id", accessor: "id" },
  { name: "name", title: "Name", accessor: "name" },
  { name: "currency", title: "Currency", accessor: "currency.name" },
  { name: "currencyCode", title: "Currency Code", accessor: "currency.code" },
  { name: "country", title: "Country", accessor: "country.name" },
  { name: "sector", title: "Sector", accessor: "subSector.sector" },
  { name: "subsector", title: "Subsector", accessor: "subSector.name" },
  { name: "investmentRole", title: "Investment Role", accessor: "investmentRole.name" },
  { name: "dealType", title: "Deal Type", accessor: "dealType.name" },
  { name: "assetType", title: "Asset Type", accessor: "type.name" },
  { name: "manager", title: "Manager", accessor: "manager.manager" },
  { name: "acquisitionDate", title: "Acquisition Date", accessor: "acquisitionDate" },
  { name: "exitDate", title: "Exit Date", accessor: "exitDate" },
  { name: "fundFDStake", title: "FD Stake", accessor: "fundFDStake" },
  { name: "fundCommitment", title: "Commitment", accessor: "fundCommitment" },
  { name: "ceo", title: "CEO", accessor: "ceo.manager" },
  { name: "cfo", title: "CFO", accessor: "cfo.manager" },
  { name: "financialYearEnd", title: "Financial Year End", accessor: "financialYearEnd" },
];

Utils.underlyingAssetFields = [
  { name: "id", title: "Id", accessor: "id" },
  { name: "name", title: "Name", accessor: "name" },
  { name: "currency", title: "Currency", accessor: "currency.name" },
  { name: "currencyCode", title: "Currency Code", accessor: "currency.code" },
  { name: "country", title: "Country", accessor: "country.name" },
  { name: "sector", title: "Sector", accessor: "subSector.sector" },
  { name: "subsector", title: "Subsector", accessor: "subSector.name" },
  { name: "investmentRole", title: "Investment Role", accessor: "investmentRole.name" },
  { name: "dealType", title: "Deal Type", accessor: "dealType.name" },
  { name: "assetType", title: "Asset Type", accessor: "type.name" },
  { name: "manager", title: "Manager", accessor: "manager.manager" },
  { name: "ceo", title: "CEO", accessor: "ceo.manager" },
  { name: "cfo", title: "CFO", accessor: "cfo.manager" },
  { name: "financialYearEnd", title: "Financial Year End", accessor: "financialYearEnd" },
];


Utils.investorsFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "code", title: "Code" },
  { name: "country.code", title: "Country Code" },
  { name: "country.name", title: "Country" },
  { name: "investorType.category", title: "Investor Type Category Money" },
  { name: "investorType.investorType", title: "Investor Type" },
  { name: "investorType.investorTypeName", title: "Investor Type Name" },
  { name: "partnershipIds", title: "Partnership IDs" },
  { name: "partnershipNames", title: "Partnership Pools" },
  { name: "fundIds", title: "Fund IDs" },
  { name: "fundNames", title: "Fund Names" }
];

Utils.investorBankFields = [
  { name: "beneficiary", title: "Beneficiary Name" },
  { name: "bank", title: "Bank Name" },
  { name: "accountNumber", title: "Account Number" },
  { name: "bankAccountId", title: "Bank Account Id" },
  { name: "IBAN", title: "IBAN" },
  { name: "bankBranch.name", title: "Bank Branch Name" },
  { name: "bankBranch.aba", title: "Bank Branch ABA" },
  { name: "bankBranch.swift", title: "Bank Branch Swift" },
  { name: "bankBranch.branchCodeType", title: "Branch Code Type" },
  { name: "bankBranch.branchCode", title: "Branch Code" },
  { name: "bankAddress", title: "Bank Address" },
  { name: "reference", title: "Reference" },
  { name: "accountHolderAddress.firstLine", title: "Account Holder Address First Line" },
  { name: "accountHolderAddress.secondLine", title: "Account Holder Address Second Line" },
  { name: "accountHolderAddress.city", title: "Account Holder Address City" },
  { name: "accountHolderAddress.state", title: "Account Holder Address State" },
  { name: "accountHolderAddress.country", title: "Account Holder Address Country" },
  { name: "accountHolderAddress.postalCode", title: "Account Holder Address Postal Code" },
  { name: "correspondentBank", title: "Correspondent Bank Name" },
  { name: "correspondentAccountNumber", title: "Correspondent Account Number" },
  { name: "correspondentIBAN", title: "Correspondent IBAN" },
  { name: "correspondentBankBranch.name", title: "Correspondent Bank Branch Name" },
  { name: "correspondentBankBranch.swift", title: "Correspondent Bank Branch Swift" },
  { name: "correspondentBankBranch.branchCodeType", title: "Correspondent Branch Code Type" },
  { name: "correspondentBankBranch.branchCode", title: "Correspondent Branch Code" },
  { name: "correspondentBankAddress", title: "Correspondent Bank Address" }
];

Utils.investorContactFields = [
  { name: "email", title: "Contact Email" },
  { name: "phone", title: "Contact Phone" },
  { name: "contactAddress.firstLine", title: "Contact Address First Line" },
  { name: "contactAddress.secondLine", title: "Contact Address Second Line" },
  { name: "contactAddress.city", title: "Contact Address City" },
  { name: "contactAddress.state", title: "Contact Address State" },
  { name: "contactAddress.country", title: "Contact Address Country" },
  { name: "contactAddress.postalCode", title: "Contact Address Postal Code" },
  { name: "registeredAddress.firstLine", title: "Registered Address First Line" },
  { name: "registeredAddress.secondLine", title: "registeredAddress Address Second Line" },
  { name: "registeredAddress.city", title: "Registered Address City" },
  { name: "registeredAddress.state", title: "registeredAddress Address State" },
  { name: "registeredAddress.country", title: "Registered Address Country" },
  { name: "registeredAddress.postalCode", title: "Registered Address Postal Code" }
];

Utils.investorTaxFields = [
  { name: "legalName", title: "Legal Name" },
  { name: "identificationType", title: "Identification Type" },
  { name: "registration", title: "Identity / Registration" },
  { name: "entityType", title: "Entity Type" },
  { name: "birthDate", title: "Birth Date" },
  { name: "residenceTaxId", title: "Tax ID in Residence" },
  { name: "domicileTaxId", title: "Tax ID in Domicile" },
  { name: "taxResidence.countryName", title: "Tax Address Country Name" },
  { name: "taxResidence.countryCode", title: "Tax Address Country Code" }
];

Utils.investorFields = [...Utils.investorsFields, ...Utils.investorBankFields, ...Utils.investorContactFields, ...Utils.investorTaxFields];

Utils.fundFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "type", title: "Type" },
  { name: "initialClosingDate", title: "Initial Closing Date" },
  { name: "currency.currencyName", title: "Currency" },
  { name: "currency.currencyCode", title: "Currency Code" },
  { name: "acquisitionDate", title: "Acquisition Date" },
  { name: "exitDate", title: "Exit Date" },
  { name: "fdStake", title: "FD Stake" },
  { name: "commitment", title: "Commitment" }
];

Utils.roundFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "date", title: "Date" },
  { name: "role", title: "Role" },
  { name: "newRaise", title: "New Raise" },
  { name: "preMoney", title: "Pre Money" },
  { name: "postMoney", title: "Post Money" },
  { name: "existingShares", title: "Existing Shares" },
  { name: "instruments", title: "Instruments" }
];

Utils.assetAccountsFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "code", title: "Code" },
  { name: "currency.name", title: "Currency" },
  { name: "currency.code", title: "Currency Code" },
  { name: "seniority", title: "Seniority" },
  { name: "type", title: "Type", accessor: "instrumentType" },
  { name: "asset.name", title: "Asset" },
  { name: "asset.id", title: "Asset Id" }
];

Utils.assetAccountFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "code", title: "Code" },
  { name: "currency.currencyName", title: "Currency" },
  { name: "currency.currencyCode", title: "Currency Code" },
  { name: "seniority", title: "Seniority" },
  { name: "type", title: "Type", accessor: "instrumentType" },
  { name: "asset.name", title: "Asset" },
  { name: "asset.id", title: "Asset Id" },
  { name: "principal", title: "Principal" },
  { name: "conversionBasis", title: "Conversion Basis" },
  { name: "conversionDiscount", title: "Conversion Discount" },
  { name: "conversionMultiple", title: "Conversion Multiple" },
  { name: "liquidationPreference", title: "Liquidation Preference" },
  { name: "valuationCap", title: "Valuation Cap" },
  { name: "termStart", title: "Term Start" },
  { name: "termEnd", title: "Term End" },
  { name: "participation", title: "Participation" },
  { name: "participationCap", title: "Participation Cap" },
  { name: "interestRate", title: "Interest Rate" },
  { name: "interestPaymentFrequency", title: "Interest Payment Frequency" },
  { name: "loanPaymentFrequency", title: "Loan Payment Frequency" },
  { name: "strikePrice", title: "Strike Price" },
  { name: "expiryDate", title: "Expiry Date" },
  { name: "issueRight", title: "Issue Right" },
  { name: "calendarType", title: "CalendarType" },
  { name: "conversionDate", title: "Conversion Date" },
  { name: "conversionInstrument", title: "Conversion Instrument" },
  { name: "conversionInstrumentId", title: "Conversion Instrument Id" },
  { name: "moratoriumFrequency", title: "Moratorium Frequency" },
  { name: "moratoriumInterestPaid", title: "Moratorium Interest Paid" },
  { name: "moratoriumPeriods", title: "Moratorium Periods" }
];

Utils.noticegroupFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "amount", title: "Amount" },
  { name: "distributionAmount", title: "Distribution Amount" },
  { name: "drawdownAmount", title: "Drawdown Amount" },
  { name: "dueDate", title: "Due Date" },
  { name: "noticeDate", title: "Notice Date" },
  { name: "purpose", title: "Purpose" },
  { name: "settledAmount", title: "Settled Amount" },
  { name: "taxAmount", title: "Tax Amount" },
  { name: "type", title: "Type" }
];

Utils.accountCodeFields = [
  { name: "name", title: "Name" },
  { name: "code", title: "Code" },
  { name: "chartOfAccounts", title: "Chart of Accounts" },
  { name: "balanceAccountType", title: "Balance Account Type" },
  { name: "accountGroup.name", title: "Account Group Name" },
  { name: "accountGroup.id", title: "Account Group Id" },
  { name: "pattern", title: "Pattern" }
];

Utils.journalLineFields = [
  { name: "id", title: "Id" },
  { name: "amount", title: "Amount" },
  { name: "accountCode", title: "Account Code" },
  { name: "fullAccountCode", title: "Full Account Code" },
  { name: "drCr", title: "Debit/Credit" },
  { name: "description", title: "Description" },
  { name: "asset.name", title: "Asset Name" },
  { name: "asset.id", title: "Asset Id" },
  { name: "assetAccount.name", title: "AssetAccount Name" },
  { name: "assetAccount.id", title: "AssetAccount Id" },
  { name: "fund.name", title: "Fund Name" },
  { name: "fund.id", title: "Fund Id" },
  { name: "investor.name", title: "Investor Name" },
  { name: "investor.id", title: "Investor Id" },
  { name: "partnership.name", title: "Partnership Name" },
  { name: "partnership.id", title: "Partnership Id" },
  { name: "journal.id", title: "Journal Id" },
  { name: "journal.number", title: "Journal Number" },
  { name: "journal.date", title: "Journal Date" },
  { name: "journal.balanceAccountCode", title: "Balance Account Code" },
  { name: "journal.description", title: "Journal Description" },
  { name: "analysisCode.id", title: "AnalysisCode Id" },
  { name: "analysisCode.name", title: "AnalysisCode Name" },
  { name: "analysisCode.type", title: "AnalysisCode Type" }
];

Utils.vendorFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "type", title: "Type" }
];

Utils.leadFields = [
  { name: "id", title: "ID", key: "id" },
  { name: "contactEmail", title: "Contact", key: "contactEmail" },
  { name: "status", title: "Status", key: "status" },
];

Utils.fundTransactionFields = [
  { name: "id", title: "ID" },
  { name: "amount", title: "Amount" },
  { name: "date", title: "Date" },
  { name: "amountTransactionCurrency", title: "Amount Transaction Currency" },
  { name: "assetAccount.name", title: "AssetAccount Name" },
  { name: "assetAccount.id", title: "AssetAccount Id" },
  { name: "assetAccount.asset.name", title: "Asset Name" },
  { name: "assetAccount.asset.id", title: "Asset Id" },
  { name: "balanceAccountCode.balanceAccountType", title: "Balance Account Type" },
  { name: "balanceAccountCode.accountCode.code", title: "Balance Account Code" },
  { name: "currency.name", title: "Currency Name" },
  { name: "currency.code", title: "Currency Code" },
  { name: "currexRate", title: "Currency Exchange Rate" },
  { name: "round.id", title: "Round Id" },
  { name: "shares", title: "Shares" },
  { name: "sharePrice", title: "Share Price" },
  { name: "sharePriceTransactionCurrency", title: "Share Price Transaction Currency" },
  { name: "fund.name", title: "Fund Name" },
  { name: "fund.id", title: "Fund Id" },
  { name: "partnership.name", title: "Partnership Name" },
  { name: "partnership.id", title: "Partnership Id" },
  { name: "type.name", title: "Type" },
  { name: "type.code", title: "Type Code" },
  { name: "group.name", title: "Group Name" },
  { name: "group.id", title: "Group Id" },
  { name: "journal.id", title: "Journal Id" },
  { name: "journal.number", title: "Journal Number" },
  { name: "journal.date", title: "Journal Date" },
  { name: "journal.description", title: "Journal Description" },
  { name: "serialNumber", title: "Serial Number" },
  { name: "taxLotDate", title: "Tax Lot Date" },
  { name: "method", title: "Method" }
];

Utils.fundTranasactionTypeFields = [
  { name: "type", title: "Type" },
  { name: "name", title: "Name" },
  { name: "drCr", title: "Debit/Credit" },
  { name: "chartOfAccounts", title: "Country" },
  { name: "category.categoryClass", title: "Category Class" },
  { name: "category.id", title: "Category Id" },
  { name: "accountCode.name", title: "Account Code Name" },
  { name: "accountCode.code", title: "Account Code" }
];

Utils.captableFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "assetId", title: "Asset ID" },
  { name: "country", title: "Country" },
  { name: "shareholderId", title: "Shareholder Id" },
  { name: "shareholderName", title: "Shareholder Name" },
  { name: "shares", title: "Shares" },
  { name: "nominalShares", title: "Nominal Shares" },
  { name: "ownership", title: "Ownership" },
  { name: "fdStake", title: "FD Stake" },
  { name: "amount", title: "Amount" },
  { name: "options", title: "Options" },
  { name: "warrants", title: "Warrants" }
];

Utils.shareholderFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "email", title: "Email" },
  { name: "country", title: "Country" },
  { name: "fundId", title: "Fund ID" }
];

Utils.forecastScenarioFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" }
];

Utils.forecastRoundFields = [
  { name: "id", title: "ID" },
  { name: "name", title: "Name" },
  { name: "date", title: "Date" },
  { name: "preMoney", title: "Pre Money Valuation" },
  { name: "raising", title: "Raising Amount" },
  { name: "scenarioId", title: "Scenario Id" },
  { name: "captableId", title: "Captable Id" }
];

Utils.fundUpdatesFields = [
  { name: "id", title: "ID" },
  { name: "text", title: "Text" },
  { name: "createDate", title: "Creation Date" },
  { name: "user.userName", title: "User's name" },
  { name: "user.userId", title: "User Id" },
  { name: "fundUpdateType.fundUpdateType", title: "Fund Update Type" },
  { name: "fundUpdateType.fundUpdateTypeId", title: "Fund Update Type Id" },
  { name: "period.date", title: "Period Date" },
  { name: "period.periodType.id", title: "Period Type" },
  { name: "significant", title: "Significant" }
];

Utils.submissionUpdatesFields = [
  { name: "text", title: "Text" },
  { name: "name", title: "Name" },
  { name: "significant", title: "Significant" },
  { name: "author.userName", title: "Author's name" },
  { name: "author.userId", title: "Author Id" },
  { name: "updateTypeDate", title: "Update Date" }
];

Utils.assetUpdatesFields = [
  { name: "id", title: "ID" },
  { name: "text", title: "Text" },
  { name: "name", title: "Name" },
  { name: "createDate", title: "Creation Date" },
  { name: "significant", title: "Significant" },
  { name: "user.userName", title: "User's name" },
  { name: "user.userId", title: "User Id" },
  { name: "assetUpdateType.assetUpdateType", title: "Asset Update Type" },
  { name: "assetUpdateType.assetUpdateTypeId", title: "Asset Update Type Id" },
  { name: "period.date", title: "Period Date" },
  { name: "period.periodType.id", title: "Period Type" }
];

Utils.dealNotesFields = [
  { name: "id", title: "Note Id" },
  { name: "title", title: "Note Title" },
  { name: "date", title: "Posted Date" },
  { name: "author.name", title: "Author name" },
  { name: "author.email", title: "Author email" },
  { name: "editedDate", title: "Note Edited Date" },
  { name: "dealId", title: "Deal Id" },
  { name: "dealName", title: "Deal Name" }
];

Utils.investorsTransactionsFields = [
  { name: "transactionId", title: "Transaction ID" },
  { name: "amount", title: "Transaction Amount" },
  { name: "transactionDate", title: "Transaction Date" },
  { name: "transactionType.type", title: "Transaction Type" },
  { name: "transactionType.name", title: "Transaction Name" },
  { name: "transactionType.drCr", title: "Debit/Credit" },
  { name: "transactionType.accountCode.code", title: "Account Code" },
  { name: "transactionType.accountCode.name", title: "Account Code Name" },
  { name: "investor.id", title: "Investor ID" },
  { name: "investor.name", title: "Investor Name" },
  { name: "investor.code", title: "Investor Code" },
  { name: "partnership.name", title: "Partnership Name" },
  { name: "partnership.id", title: "Partnership ID" },
  { name: "partnership.fund.fundId", title: "Fund ID" },
  { name: "partnership.fund.fundName", title: "Fund Name" },
  { name: "notice.noticeId", title: "Notice ID" },
  { name: "notice.type.id", title: "Notice Type ID" },
  { name: "notice.type.name", title: "Notice Type Name" },
  { name: "notice.noticeGroup.id", title: "Notice Group ID" },
  { name: "notice.noticeGroup.name", title: "Notice Group Name" },
  { name: "notice.noticeGroup.noticeDate", title: "Notice Group Date" },
  { name: "notice.journal.id", title: "Journal ID" },
  { name: "notice.journal.number", title: "Journal Number" },
  { name: "notice.journal.date", title: "Journal Date" },
  { name: "notice.journal.narration", title: "Journal Description" },
  { name: "notice.journal.drcr", title: "Journal Debit/Credit" },
  { name: "notice.journal.balanceAccountCode.balanceAccountType", title: "Balance Account Type" },
  { name: "notice.journal.balanceAccountCode.accountCode.code", title: "Balance Account Code" },
  { name: "notice.journal.balanceAccountCode.accountCode.name", title: "Balance Account Name" },
  { name: "parentNoticeGroup.id", title: "Parent Notice Group ID" },
  { name: "parentNoticeGroup.name", title: "Parent Notice Group Name" },
  { name: "parentNoticeGroup.noticeDate", title: "Parent Notice Group Date" },
  { name: "parentNoticeGroup.type.id", title: "Parent Notice Type ID" },
  { name: "parentNoticeGroup.type.name", title: "Parent Notice Type Name" },
];


Utils.investorTransactionTypesFields = [
  { name: "type", title: "Type" },
  { name: "name", title: "Name" },
  { name: "drCr", title: "Debit/Credit" },
  { name: "chartOfAccounts", title: "Chart Of Accounts" },
  { name: "category", title: "Category" },
  { name: "accountCode.name", title: "Account Code Name" },
  { name: "accountCode.code", title: "Account Code" },
];

Utils.aiFields = [
  { name: "answer", title: "AI answer" },
];

Utils.mcpClientFields = [
  { name: "answer", title: "MCP Client answer" },
];

Utils.activityFields = [
  { name: "title", title: "Title", accessor: "title" }, 
  { name: "date", title: "Date", accessor: "date" },
  { name: "description", title: "Description", accessor: "description" },
  { name: "type", title: "Type", accessor: "type" },
  { name: "email", title: "User Email", accessor: "user.username" },
  { name: "firstname", title: "First Name", accessor: "user.firstname" },
  { name: "lastname", title: "Last Name", accessor: "user.lastname" },
  { name: "entitytype", title: "Entity Type", accessor: "entity.type" },
  { name: "entityid", title: "Entity ID", accessor: "entity.id" },
  { name: "context", title: "Context Type", accessor: "context.type" },
  { name: "contextid", title: "Context Id", accessor: "context.id" },
  { name: "team", title: "Team Name", accessor: "team.id" }
];


Utils.fields = {
  asset: Utils.assetFields,
  assets: Utils.assetFields,
  underlyingasset: Utils.underlyingAssetFields,
  underlyingassets: Utils.underlyingAssetFields,
  fund: Utils.fundFields,
  funds: Utils.fundFields,
  investor: Utils.investorFields,
  investors: Utils.investorsFields,
  dealnote: Utils.dealNotesFields,
  dealnotes: Utils.dealNotesFields,
  round: Utils.roundFields,
  rounds: Utils.roundFields,
  noticegroup: Utils.noticegroupFields,
  noticegroups: Utils.noticegroupFields,
  captable: Utils.captableFields,
  captables: Utils.captableFields,
  shareholder: Utils.shareholderFields,
  shareholders: Utils.shareholderFields,
  forecastscenario: Utils.forecastScenarioFields,
  forecastscenarios: Utils.forecastScenarioFields,
  forecastround: Utils.forecastRoundFields,
  forecastrounds: Utils.forecastRoundFields,
  assetaccount: Utils.assetAccountFields,
  assetaccounts: Utils.assetAccountsFields,
  accountcode: Utils.accountCodeFields,
  accountcodes: Utils.accountCodeFields,
  journalline: Utils.journalLineFields,
  journallines: Utils.journalLineFields,
  fundtransaction: Utils.fundTransactionFields,
  fundtransactions: Utils.fundTransactionFields,
  fundtransactiontype: Utils.fundTranasactionTypeFields,
  fundtransactiontypes: Utils.fundTranasactionTypeFields,
  fundupdate: Utils.fundUpdatesFields,
  fundupdates: Utils.fundUpdatesFields,
  assetupdate: Utils.assetUpdatesFields,
  assetupdates: Utils.assetUpdatesFields,
  submissionupdate: Utils.submissionUpdatesFields,
  submissionupdates: Utils.submissionUpdatesFields,
  investortransaction: Utils.investorsTransactionsFields,
  investortransactions: Utils.investorsTransactionsFields,
  investortransactiontypes: Utils.investorTransactionTypesFields,
  ai: Utils.aiFields,
  mcp: Utils.mcpClientFields,
  activity: Utils.activityFields,
};

export { Utils };
